import { cva, type VariantProps } from 'class-variance-authority';
import { focusRing, transitionBase, uiMarker, uiMarkerLayout } from './shared';

/**
 * SURFACE / CARD
 *
 * Every panel, card and section container in the app. `elevation` says how far
 * off the page it floats; `padding` and `radius` are separate so a card can be
 * flush-edged in a table cell but rounded on a dashboard.
 */
/*
 * The background lives in `tone`, not in the base.
 *
 * It used to be in the base, which meant every Surface painted `bg-card`
 * whether or not it wanted one — and combined with `elevation: 'floating'`
 * that made the component inapplicable to all 191 panel-shaped divs in the
 * product. Every one of them would have gained a background, a shadow, or
 * both. A component that cannot be adopted anywhere is not a strict component,
 * it is a broken default, and 0 uses was the evidence.
 */
export const surfaceRecipe = cva([uiMarker], {
  variants: {
    elevation: {
      /** Flat — sits directly on the background, no shadow. */
      flat: 'shadow-e0',
      /** A hairline of separation. Table containers, inline panels. */
      raised: 'shadow-e1',
      /** The default card. */
      floating: 'shadow-e2',
      /** Modals, popovers, anything overlapping the page. */
      overlay: 'shadow-e3',
    },
    bordered: {
      true: 'border border-border',
      false: '',
      /**
       * A place something could go but nothing is yet: the studio's empty
       * panels, a hidden service that is still editable but switched off.
       */
      dashed: 'border border-dashed border-border',
    },
    radius: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      /** Fully rounded ends — a counter chip, a floating pill of controls. */
      pill: 'rounded-pill',
    },
    padding: {
      none: 'p-0',
      /** 12 across, 8 down: a rule row that sits in a grid beside 32-high fields. */
      xs: 'px-3 py-2',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    },
    /** Lifts on hover. Only use when the whole surface is clickable. */
    interactive: {
      true: `cursor-pointer hover:border-ring hover:shadow-e2 ${transitionBase} transition-shadow`,
      false: '',
    },
    tone: {
      /** A card plane: the component's own background. */
      default: 'bg-card text-card-foreground',
      /** No background at all — an outlined panel over whatever is behind it. */
      none: '',
      /**
       * The shell's own ground: the strip the sidebars sit on, a shade off
       * the page so the rails read as frame rather than content. Both rails
       * take it, which is the only way they match.
       */
      canvas: 'bg-canvas text-foreground',
      muted: 'bg-muted text-foreground',
      success:
        'bg-success-subtle text-success-subtle-foreground border-success/20',
      warning:
        'bg-warning-subtle text-warning-subtle-foreground border-warning/20',
      info: 'bg-info-subtle text-info-subtle-foreground border-info/20',
      destructive:
        'bg-destructive-subtle text-destructive-subtle-foreground border-destructive/25',
      /**
       * A translucent dark wash laid over a picture, with white on it — the
       * play glyph on a video thumbnail, the spinner on a photo still
       * uploading, the page counter in the lightbox. Dark in both themes for
       * the reason the lightbox stage is: it sits on the image, not on a
       * themed surface, and a wash that flipped with the theme would vanish
       * on a light photo in dark mode.
       */
      // design-tokens-ignore -- sits on a photo, not on a themed surface; see above.
      scrim: 'bg-black/50 text-white',
      /**
       * A sheet of paper: stays light in either theme because what is drawn
       * on it leaves the app — the signature pad, whose bitmap is exported
       * white with black ink and printed on the release form. The same idea
       * as InvoicePreview's `paper`; a themed surface here would put a dark
       * frame around a permanently white signature.
       */
      // design-tokens-ignore -- print output; the app's theme must not reach it.
      paper: 'light bg-white text-foreground',
    },
    /**
     * A coloured rule around the whole surface, for the card that is *the
     * one* among its siblings — the plan the account is on, the option being
     * recommended. Always 2px: at the hairline width a card border is drawn
     * at, a change of colour reads as a slightly different grey rather than
     * as a state. PlanCard's `emphasis` is the same rule in primary.
     */
    accent: {
      none: '',
      primary: 'border-2 border-primary',
      success: 'border-2 border-success',
    },
  },
  defaultVariants: {
    // Flat, because a bordered panel is the common case and a shadow is the
    // thing a call site should have to ask for.
    elevation: 'flat',
    bordered: true,
    radius: 'xl',
    padding: 'none',
    interactive: false,
    tone: 'default',
    accent: 'none',
  },
});

export type SurfaceRecipeProps = VariantProps<typeof surfaceRecipe>;

/**
 * A Surface drawn as a button (`as="button"`), the whole panel the thing to
 * tap: the phone's Create a call lists who is on it in one, and a tap opens
 * the operators to change. It resets the button to sit as the div would, a
 * block at the panel's width with its contents at the left, and gives it
 * the focus ring. Its own recipe rather than a variant of surfaceRecipe,
 * because `w-full` on a Surface that stays a div is placement, not a way
 * of asking for a button.
 */
export const pressablePanelRecipe = cva([
  uiMarker,
  'block w-full text-left',
  focusRing,
]);

/** Header block inside a Card: title, description, optional actions. */
export const cardHeaderRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex items-start justify-between gap-4'],
  {
    variants: {
      padding: {
        none: '',
        sm: 'p-3 pb-0',
        md: 'p-4 pb-0',
        lg: 'p-6 pb-0',
      },
      /**
       * A rule under the header, for a card whose head is a bar rather than a
       * title floating above the body. Six invoice cards drew this by hand as
       * `p-3 border-b border-border`, which also meant restoring the bottom
       * padding the `pb-0` above removes.
       *
       * Same name and meaning as DialogHeader's `divided`.
       */
      divided: {
        true: 'border-b border-border',
        false: '',
      },
      /**
       * The header as a tinted band across the top of the card — the section
       * panels on the call profile, where the title sits on the same muted
       * wash the table header uses. Pair it with `divided`; a tint with no
       * rule under it reads as a stain rather than a bar.
       */
      tone: {
        default: '',
        muted: 'bg-muted/50',
      },
      /**
       * Where the text sits. `center` is for a card that is the whole screen
       * — an invitation, a confirmation, a "we sent you a link" — whose
       * title and description are read as a message rather than as the head
       * of a table. Five such cards wrote `text-center` on the header, which
       * did nothing once the header became a start-aligned column: each
       * line shrank to its own width and sat on the left.
       */
      align: {
        start: '',
        center: 'items-center text-center',
      },
    },
    compoundVariants: [
      // pb-0 exists so a title sits tight above its body. With a rule under
      // it, the header is its own band and needs its padding back.
      { divided: true, padding: 'sm', class: 'pb-3' },
      { divided: true, padding: 'md', class: 'pb-4' },
      { divided: true, padding: 'lg', class: 'pb-6' },
    ],
    defaultVariants: {
      padding: 'lg',
      divided: false,
      tone: 'default',
      align: 'start',
    },
  }
);

export const cardBodyRecipe = cva([uiMarker, uiMarkerLayout, ''], {
  variants: {
    padding: {
      none: '',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    },
  },
  defaultVariants: { padding: 'lg' },
});

export const cardFooterRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex items-center gap-2'],
  {
    variants: {
      padding: {
        none: '',
        sm: 'p-3 pt-0',
        md: 'p-4 pt-0',
        lg: 'p-6 pt-0',
      },
      divided: {
        true: 'border-t border-border',
        false: '',
      },
    },
    defaultVariants: { padding: 'lg', divided: false },
  }
);

/**
 * PAGE HEADER
 *
 * The title block at the top of every screen. Stacks on mobile, sits on one
 * line from `sm` up.
 */
export const pageHeaderRecipe = cva([uiMarker, uiMarkerLayout, 'flex gap-3'], {
  variants: {
    divided: {
      true: 'border-b border-border pb-4',
      false: '',
    },
    /**
     * `stacked` puts the actions under the title on a narrow screen and
     * beside it from `sm` up, for a desk page that can be opened on a
     * phone. `row` keeps them beside the title at every width, as the
     * mobile app's screens draw "Today's activity" and "+ Call": stacked,
     * a phone (always under `sm`) dropped the button onto a line of its own.
     */
    layout: {
      stacked: 'flex-col sm:flex-row sm:items-start sm:justify-between',
      row: 'flex-row items-center justify-between',
    },
  },
  defaultVariants: { divided: false, layout: 'stacked' },
});

/**
 * PINNED ACTIONS
 *
 * A detail card's buttons, pinned to the top of the scrolling column so Print
 * and Save stay in reach at the bottom of a long list while the title
 * scrolls away like everything else. A pinned box can only travel inside its
 * parent, so it sits directly in the tall column rather than on the title
 * row. `collapsed` gives it no height and takes the column's gap back (the
 * column is a `Stack gap={6}`), so the buttons land on the title line exactly
 * as if they were part of it; with a warning above the title they keep their
 * own line instead of hanging over it.
 */
export const pinnedActionsRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'sticky top-0 z-10 flex flex-col self-end overflow-visible',
  ],
  {
    variants: {
      collapsed: { true: '-mb-6 h-0', false: '' },
    },
    defaultVariants: { collapsed: true },
  }
);
export type PinnedActionsRecipeProps = VariantProps<typeof pinnedActionsRecipe>;

/** The row of buttons: shrink-wrapped, on the card's own background so the title cannot show through. */
export const pinnedActionsRowRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex w-fit items-center gap-2 bg-card',
]);
